import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import { authAPI } from "../../services/api";
import { Eye, EyeOff, LogIn, User, Lock } from "lucide-react";

export default function Auth() {
  const { company, setCurrentUser, isLoggedIn, loading } = useApp();
  const navigate = useNavigate();
  const [dark, setDark] = useState(true);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!loading && isLoggedIn) navigate("/dashboard");
  }, [isLoggedIn, loading, navigate]);

  const handleLogin = async () => {
    if (!username || !password) {
      setError("لطفاً نام کاربری و پسورد را وارد کنید");
      return;
    }
    setIsLoading(true);
    setError("");

    try {
      const res = await authAPI.login(username, password, remember);
      if (res.success) {
        localStorage.setItem("auth_token", res.token);
        setCurrentUser({
          id: res.user.id,
          username: res.user.username,
          fullName: res.user.fullName || res.user.full_name,
          email: res.user.email,
          phone: res.user.phone,
          role: res.user.role,
          avatar: res.user.avatar,
          permissions: res.user.permissions || {},
        });
        navigate("/dashboard");
      } else {
        setError(res.message || "نام کاربری یا پسورد اشتباه است");
      }
    } catch {
      setError("خطا در اتصال به سرور. لطفاً دوباره تلاش کنید");
    }
    setIsLoading(false);
  };

  if (loading) {
    return (
      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
        background: "linear-gradient(160deg,#070714 0%,#0e0e2a 100%)",
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 48, height: 48, border: "3px solid rgba(59,130,246,0.2)", borderTop: "3px solid #3b82f6", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
          <div style={{ color: "#64748b", fontSize: 14 }}>در حال بررسی...</div>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div dir="rtl" style={{
      minHeight: "100vh", display: "flex",
      fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
      position: "relative", overflow: "hidden",
      background: dark
        ? "linear-gradient(160deg,#070714 0%,#0e0e2a 100%)"
        : "linear-gradient(160deg,#f8faff 0%,#eef2ff 100%)",
    }}>

      {/* Background */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        <div style={{ position: "absolute", width: 700, height: 700, borderRadius: "50%", filter: "blur(130px)", background: dark ? "rgba(59,130,246,0.09)" : "rgba(59,130,246,0.06)", top: "-200px", right: "-100px" }} />
        <div style={{ position: "absolute", width: 600, height: 600, borderRadius: "50%", filter: "blur(120px)", background: dark ? "rgba(139,92,246,0.08)" : "rgba(139,92,246,0.05)", bottom: "-150px", left: "-100px" }} />
        <svg viewBox="0 0 1440 900" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: dark ? 0.35 : 0.2 }} preserveAspectRatio="xMidYMid slice">
          <defs>
            <linearGradient id="lg1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={dark ? "#1e1b4b" : "#c7d2fe"} stopOpacity="1" />
              <stop offset="100%" stopColor={dark ? "#0f172a" : "#e0e7ff"} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d="M0,0 L0,520 Q200,420 400,500 Q600,580 800,460 Q1000,340 1200,440 Q1380,520 1440,480 L1440,0 Z" fill="url(#lg1)" />
        </svg>
      </div>

      {/* Dark/Light toggle */}
      <button onClick={() => setDark(!dark)} style={{
        position: "absolute", top: 20, left: 20, zIndex: 10,
        width: 40, height: 40, borderRadius: 10,
        background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
        border: `1px solid ${dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
        cursor: "pointer", fontSize: 18,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {dark ? "☀️" : "🌙"}
      </button>

      {/* LEFT */}
      <div style={{
        flex: 1, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        padding: "60px 40px", position: "relative",
        borderLeft: `1px solid ${dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
      }} className="login-left">
        <div style={{ maxWidth: 440, width: "100%" }}>
          <div style={{
            width: 90, height: 90, borderRadius: 24,
            background: "linear-gradient(135deg,#3b82f6,#8b5cf6)",
            display: "flex", alignItems: "center", justifyContent: "center",
            marginBottom: 28, boxShadow: "0 12px 40px rgba(59,130,246,0.4)",
            overflow: "hidden",
          }}>
            {company.logo
              ? <img src={company.logo} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <span style={{ fontSize: 40, fontWeight: 900, color: "white" }}>{company.name.charAt(0)}</span>
            }
          </div>
          <h1 style={{ fontSize: 34, fontWeight: 900, color: dark ? "#f1f5f9" : "#0f172a", margin: "0 0 10px" }}>
            {company.name}
          </h1>
          <p style={{ fontSize: 15, color: dark ? "#64748b" : "#94a3b8", margin: "0 0 44px", lineHeight: 1.8 }}>
            سیستم جامع مدیریت کسب‌وکار
          </p>
          {[
            { icon: "🔐", title: "امنیت پیشرفته", desc: "کنترل دسترسی و ثبت فعالیت‌ها" },
            { icon: "📊", title: "گزارش‌های دقیق", desc: "تحلیل کامل عملکرد کسب‌وکار" },
            { icon: "👥", title: "چند کاربره", desc: "مدیریت تیم با نقش‌های مختلف" },
            { icon: "💰", title: "چند ارزی", desc: "پشتیبانی از ارزهای مختلف" },
          ].map((f, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 14,
              padding: "13px 16px", borderRadius: 12, marginBottom: 8,
              background: dark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.8)",
              border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
            }}>
              <span style={{ fontSize: 22 }}>{f.icon}</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: dark ? "#e2e8f0" : "#0f172a" }}>{f.title}</div>
                <div style={{ fontSize: 11, color: dark ? "#64748b" : "#94a3b8", marginTop: 2 }}>{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* RIGHT - فرم */}
      <div style={{
        width: "100%", maxWidth: 500,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "40px 32px",
      }}>
        <div style={{ width: "100%", maxWidth: 420 }}>
          <div style={{
            borderRadius: 24, padding: "44px 40px",
            background: dark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.98)",
            border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
            backdropFilter: "blur(24px)",
            boxShadow: dark ? "0 32px 100px rgba(0,0,0,0.6)" : "0 32px 100px rgba(0,0,0,0.12)",
          }}>
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <div style={{
                width: 52, height: 52, borderRadius: 14,
                background: "linear-gradient(135deg,#3b82f6,#8b5cf6)",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 16px", overflow: "hidden",
                boxShadow: "0 4px 16px rgba(59,130,246,0.4)",
              }}>
                {company.logo
                  ? <img src={company.logo} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : <span style={{ fontSize: 22, fontWeight: 900, color: "white" }}>{company.name.charAt(0)}</span>
                }
              </div>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: dark ? "#f1f5f9" : "#0f172a", margin: "0 0 6px" }}>
                ورود به سیستم
              </h2>
              <p style={{ fontSize: 13, color: dark ? "#64748b" : "#94a3b8", margin: 0 }}>
                نام کاربری و پسورد خود را وارد کنید
              </p>
            </div>

            {error && (
              <div style={{
                padding: "11px 14px", borderRadius: 10, marginBottom: 20,
                background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)",
                color: "#ef4444", fontSize: 13, textAlign: "center", fontWeight: 500,
              }}>
                ⚠️ {error}
              </div>
            )}

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: dark ? "#94a3b8" : "#64748b", display: "block", marginBottom: 7 }}>
                نام کاربری
              </label>
              <div style={{
                display: "flex", alignItems: "center", gap: 10,
                background: dark ? "rgba(255,255,255,0.05)" : "#f8faff",
                border: `1.5px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
                borderRadius: 12, padding: "11px 14px",
              }}>
                <User size={15} color={dark ? "#64748b" : "#94a3b8"} />
                <input
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleLogin()}
                  placeholder="نام کاربری را وارد کنید"
                  autoComplete="username"
                  style={{
                    flex: 1, background: "none", border: "none", outline: "none",
                    color: dark ? "#f1f5f9" : "#0f172a", fontSize: 13,
                    fontFamily: "inherit", direction: "rtl",
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: 18 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: dark ? "#94a3b8" : "#64748b", display: "block", marginBottom: 7 }}>
                پسورد
              </label>
              <div style={{
                display: "flex", alignItems: "center", gap: 10,
                background: dark ? "rgba(255,255,255,0.05)" : "#f8faff",
                border: `1.5px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
                borderRadius: 12, padding: "11px 14px",
              }}>
                <Lock size={15} color={dark ? "#64748b" : "#94a3b8"} />
                <input
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleLogin()}
                  type={showPass ? "text" : "password"}
                  placeholder="پسورد را وارد کنید"
                  autoComplete="current-password"
                  style={{
                    flex: 1, background: "none", border: "none", outline: "none",
                    color: dark ? "#f1f5f9" : "#0f172a", fontSize: 13,
                    fontFamily: "inherit", direction: "rtl",
                  }}
                />
                <button onClick={() => setShowPass(!showPass)} style={{ background: "none", border: "none", cursor: "pointer", color: dark ? "#64748b" : "#94a3b8", display: "flex", padding: 0 }}>
                  {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }} onClick={() => setRemember(!remember)}>
                <div style={{
                  width: 17, height: 17, borderRadius: 5, flexShrink: 0,
                  background: remember ? "linear-gradient(135deg,#3b82f6,#8b5cf6)" : "transparent",
                  border: `2px solid ${remember ? "#3b82f6" : dark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.2s",
                }}>
                  {remember && <span style={{ color: "white", fontSize: 10, fontWeight: 700 }}>✓</span>}
                </div>
                <span style={{ fontSize: 12, color: dark ? "#94a3b8" : "#64748b", userSelect: "none" }}>مرا به خاطر بسپار</span>
              </div>
              <span style={{ fontSize: 12, color: "#3b82f6", cursor: "pointer", fontWeight: 600 }}>
                فراموشی پسورد؟
              </span>
            </div>

            <button
              onClick={handleLogin}
              disabled={isLoading}
              style={{
                width: "100%", padding: "13px",
                background: isLoading ? "rgba(59,130,246,0.5)" : "linear-gradient(135deg,#3b82f6,#8b5cf6)",
                border: "none", borderRadius: 12,
                cursor: isLoading ? "not-allowed" : "pointer",
                color: "white", fontSize: 14, fontWeight: 700,
                fontFamily: "inherit",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                boxShadow: isLoading ? "none" : "0 4px 20px rgba(59,130,246,0.4)",
                transition: "all 0.2s",
              }}
            >
              {isLoading
                ? <div style={{ width: 18, height: 18, border: "2px solid rgba(255,255,255,0.3)", borderTop: "2px solid white", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                : <><LogIn size={17} /> ورود به سیستم</>
              }
            </button>

            <p style={{ textAlign: "center", fontSize: 11, color: dark ? "#475569" : "#94a3b8", margin: "20px 0 0" }}>
              © 2025 {company.name} — تمام حقوق محفوظ است
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 768px) { .login-left { display: none !important; } }
      `}</style>
    </div>
  );
}